import { FC } from 'react'

import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import IconSpotify from 'src/Assets/Svg/spotify.svg?react'
import TikTokIcon from 'src/Assets/Svg/tiktok.svg?react'
import TikTokIconLight from 'src/Assets/Svg/tiktok_light.svg?react'
import MetaAdsIcon from 'src/Assets/Svg/platform-icons/MetaAds.svg?react'
import SuccessIcon from 'src/Assets/Svg/pages-icons/Success.svg?react'
import { APP } from 'src/Configs/App'
import { useCreateCampaignEvents } from 'src/Hooks/Analytics/useCreateCampaignEvents'
import { useThemeMode } from 'src/Hooks/useThemeMode'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import {
  MAX_MOBILE_WIDTH,
  MAX_TABLET_WIDTH,
  variables,
} from 'src/Styled/variables'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

import { PlatformUpsellCard } from './PlatformUpsellCard'

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 40px;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    padding-bottom: 120px;
  }
`

const Welcome = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  text-align: center;
`

const WelcomeTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 600px;
`

const WelcomeTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize18};
  font-weight: ${variables.fontWeight600};
  line-height: ${variables.lineHeight150per};
`

const WelcomeText = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
`

const StyledSuccessIcon = styled(SuccessIcon)`
  color: ${({ theme }) => theme.color.brandGreen};
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.color.lightGrey};
`

const CardsRow = styled.div`
  display: flex;
  width: 100%;
  align-items: stretch;
  gap: 32px;

  @media (max-width: ${MAX_TABLET_WIDTH}) {
    flex-direction: column;
  }
`

export const EmptyCampaignState: FC = () => {
  const navigate = useNavigate()
  const { resolvedTheme } = useThemeMode()
  const TikTok = resolvedTheme === 'dark' ? TikTokIconLight : TikTokIcon
  const { campaignCreationStartMpEventHandler } = useCreateCampaignEvents()

  const handleSpotifyClick = (): void => {
    campaignCreationStartMpEventHandler({
      platformType: CampaignPlatformTypeEnum.SPOTIFY,
    })
    navigate(
      APP.PRE_RELEASE_CAMPAIGNS_ENABLED
        ? ArtistRoutesEnum.ChooseCreateCampaignReleaseType
        : ArtistRoutesEnum.ChooseCreateCampaignPlatformSpotify,
    )
  }

  const handleTikTokClick = (): void => {
    campaignCreationStartMpEventHandler({
      platformType: CampaignPlatformTypeEnum.TIKTOK,
    })
    navigate(ArtistRoutesEnum.ChooseCreateCampaignPlatformTikTok)
  }

  const handleMetaClick = (): void => {
    campaignCreationStartMpEventHandler({
      platformType: CampaignPlatformTypeEnum.META,
    })
    navigate(ArtistRoutesEnum.CreateAds)
  }

  return (
    <Wrapper>
      <Welcome>
        <StyledSuccessIcon width={40} height={40} />
        <WelcomeTextGroup>
          <WelcomeTitle>Welcome to SoundCampaign!</WelcomeTitle>
          <WelcomeText>Ready to promote your music?</WelcomeText>
          <WelcomeText>
            Choose a promotion type to expand your reach, connect with new
            listeners, and build real momentum.
          </WelcomeText>
        </WelcomeTextGroup>
      </Welcome>
      <Divider />
      <CardsRow>
        <PlatformUpsellCard
          Icon={IconSpotify}
          title='Get on Spotify playlists'
          bullets={[
            'Direct pitches to genre-matched playlist curators.',
            'Guaranteed, actionable feedback from industry pros.',
            'Build a loyal, long-term listener base organically.',
          ]}
          buttonLabel='Get on Spotify playlists'
          onClick={handleSpotifyClick}
        />
        {APP.ENABLE_META_ADS && (
          <PlatformUpsellCard
            Icon={MetaAdsIcon}
            title='Run ads that drive streams'
            bullets={[
              'Direct traffic from high-converting Meta Ads.',
              'Fast, scalable, and reliable listener acquisition.',
              'Watch your Spotify numbers grow in real-time.',
            ]}
            buttonLabel='Launch an ad campaign'
            onClick={handleMetaClick}
          />
        )}
        <PlatformUpsellCard
          Icon={TikTok}
          title='Go viral on TikTok'
          bullets={[
            'Your track lands in the hands of real content creators.',
            "When multiple videos pop off, TikTok's algorithm kicks in.",
            'That momentum turns into Spotify streams.',
          ]}
          buttonLabel='Go viral on TikTok'
          onClick={handleTikTokClick}
        />
      </CardsRow>
    </Wrapper>
  )
}
